import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, MessageSquareText, Send, ImagePlus, X,
  Leaf, Trash2, Sprout, RotateCcw,
} from "lucide-react";
import {
  EASE, Eyebrow, IMAGES, PHONE, PHONE_RAW, Reveal,
  nextFridayLabel, scrollToId,
} from "./shared";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-stone-700 bg-[#0c0a09] px-4 py-3 text-base sm:text-sm text-bone placeholder:text-stone-600 focus:border-amber-500 focus:outline-none transition-colors duration-150";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";

const AREAS = [
  "Tiny patch — smaller than a two-car garage",
  "≈ 0.1 acre — a two-car-garage lot",
  "≈ 0.25 acre — a big backyard corner",
  "≈ 0.5 acre — half a football field",
  "≈ 1 acre — a full football field",
  "More than an acre — photos tell the story",
  "No idea — that's fine, photos tell it",
];

const CLEAR_ITEMS = [
  "Brush & saplings",
  "Invasive growth / honeysuckle",
  "Fence line",
  "Trail or access path",
  "Small trees",
  "Not sure — just want it looked at",
];

const TERRAINS = ["Flat", "Sloped", "Steep"];

const COVERS = [
  "Overgrown brush & saplings",
  "Invasive growth & honeysuckle",
  "Fence lines choked with brush",
  "Small lots & building sites",
  "Trails & access paths",
  "The yard-turned-jungle cleanup",
];

const WHY = [
  {
    icon: Leaf,
    title: "Mulch stays put",
    body: "Everything gets ground where it stands into natural ground cover — it feeds the soil instead of a landfill.",
  },
  {
    icon: Trash2,
    title: "No burn pile, no dump fees",
    body: "Nothing gets hauled, burned, or stacked at the curb. The debris problem disappears with the brush.",
  },
  {
    icon: Sprout,
    title: "No bare dirt scar",
    body: "A blanket of mulch means no mud pit, no erosion, no reseeding project after the machine leaves.",
  },
];

export default function LandClearing() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [area, setArea] = useState("");
  const [items, setItems] = useState([]);
  const [terrain, setTerrain] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [spots, setSpots] = useState(null);
  const MAX_PHOTOS = 4;

  const loadSpots = () =>
    axios.get(`${API}/clearing-spots`).then((r) => setSpots(r.data)).catch(() => {});

  useEffect(() => {
    loadSpots();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleItem = (label) =>
    setItems((cur) => (cur.includes(label) ? cur.filter((c) => c !== label) : [...cur, label]));

  const onPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const accepted = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" is over 10MB — skipped.`);
        continue;
      }
      accepted.push({ file, preview: URL.createObjectURL(file), id: `${file.name}-${file.size}-${file.lastModified}` });
    }
    setPhotos((cur) => {
      const room = MAX_PHOTOS - cur.length;
      if (accepted.length > room) toast.warning(`Up to ${MAX_PHOTOS} photos — extra ones skipped.`);
      return [...cur, ...accepted.slice(0, Math.max(0, room))];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const uploadedPaths = [];
    for (const p of photos) {
      try {
        const fd = new FormData();
        fd.append("file", p.file);
        const up = await axios.post(`${API}/uploads`, fd);
        uploadedPaths.push(up.data.path);
      } catch {
        toast.warning(`"${p.file.name}" didn't upload — continuing without it.`);
      }
    }
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        address: form.address.trim(),
        job_type: "land_clearing",
        area_size: area || null,
        clear_items: items,
        terrain: terrain || null,
        notes: form.notes.trim() || null,
        photo_paths: uploadedPaths,
      });
    } catch {
      toast.error(`Something glitched — please text Eric directly at ${PHONE}.`);
      setSending(false);
      return;
    }
    toast.success("Request sent — Eric has been emailed.");
    setSending(false);
    setSent(true);
    loadSpots();
  };

  return (
    <div data-testid="land-clearing-page">
      <header className="fixed top-0 z-50 w-full border-b border-stone-800/80 bg-[#0c0a09]/85 px-4 py-3 backdrop-blur-xl sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <a href="/" data-testid="lc-brand" className="flex items-center gap-3">
            <span className="font-display text-2xl font-black uppercase leading-none tracking-tight text-bone">
              Friday<span className="text-amber-500">Stumps</span>
            </span>
            <span className="hidden items-center gap-2 border border-stone-800 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] text-stone-400 md:flex">
              <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-amber-500" />
              {nextFridayLabel()}
            </span>
          </a>
          <div className="flex items-center gap-2">
            <a href="/stump-grinding" data-testid="lc-back-home" className="hidden items-center gap-2 border border-stone-700 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500 sm:flex">
              <ArrowLeft className="h-3.5 w-3.5" /> Stump grinding
            </a>
            <button
              data-testid="lc-quote-cta"
              onClick={() => scrollToId("clearing-quote")}
              className="flex items-center gap-1.5 whitespace-nowrap bg-amber-500 px-3.5 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600"
            >
              Clearing quote <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="relative overflow-hidden px-4 pb-16 pt-32 sm:px-8 sm:pt-36">
          <img src={IMAGES.forest} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c0a09]/60 via-[#0c0a09]/80 to-[#0c0a09]" />
          <div className="relative mx-auto max-w-7xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500 sm:text-xs"
            >
              Also from FridayStumps · same Eric, bigger mess
            </motion.p>
            <h1 data-testid="lc-headline" className="font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">
              {[
                { text: "MORE THAN A STUMP?", cls: "text-bone" },
                { text: "WE CLEAR THE WHOLE MESS.", cls: "italic text-amber-500" },
              ].map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
                    className={`block ${line.cls}`}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-stone-400 sm:text-lg"
            >
              Forestry mulching &amp; land clearing across the 614. A tracked
              machine with a rotating mulching head grinds standing brush,
              saplings, invasive honeysuckle, vines, and small trees straight
              into mulch that stays on the ground. Whether you call it land
              clearing, brush clearing, brush hogging, honeysuckle removal,
              fence-line clearing, lot clearing, or trail cutting — it's the
              same type of service, the same Friday, the same contactless deal.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <div className="flex flex-col">
                <button
                  data-testid="lc-hero-quote-cta"
                  onClick={() => scrollToId("clearing-quote")}
                  className="group flex items-center gap-2 bg-amber-500 px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600"
                >
                  Ask about forestry mulching
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
                <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                  Founding-customer pricing while we build this out.
                </span>
              </div>
              <a
                data-testid="lc-hero-sms-cta"
                href={`sms:${PHONE_RAW}`}
                className="flex items-center gap-2 self-start border border-stone-700 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-stone-200 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
              >
                <MessageSquareText className="h-4 w-4" />
                Text photos to {PHONE}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
              data-testid="lc-launch-banner"
              className="mt-8 flex max-w-2xl items-start gap-3 border border-amber-500/50 bg-amber-500/10 px-5 py-4"
            >
              <span className="pulse-dot mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              <p className="font-mono text-xs leading-relaxed tracking-[0.08em] text-amber-400 sm:text-sm">
                FridayStumps is expanding into forestry mulching — and we're
                looking for our first clearing jobs. Get in now, while we're
                building this side out, and we'll price it like it.
              </p>
            </motion.div>

            {spots && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.15 }}
                data-testid="lc-spots"
                className="mt-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-stone-400"
              >
                <span className="flex gap-1.5">
                  {Array.from({ length: spots.total }).map((_, i) => (
                    <span
                      key={i}
                      data-testid={`lc-spot-pip-${i}`}
                      className={`h-2 w-6 ${i < spots.claimed ? "bg-stone-700" : "bg-amber-500"}`}
                    />
                  ))}
                </span>
                {spots.remaining > 0
                  ? `${spots.remaining} of ${spots.total} founding-pricing spots left`
                  : "Founding spots claimed — still reach out"}
              </motion.div>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <Reveal>
            <Eyebrow>What it covers</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-bone sm:text-3xl lg:text-4xl">
              If it's green and in the way, it's gone.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COVERS.map((c, i) => (
              <Reveal key={c} delay={i * 0.08}>
                <div data-testid={`lc-cover-${i}`} className="border border-stone-800 bg-charcoal px-5 py-4 font-mono text-xs uppercase tracking-[0.15em] text-stone-300 transition-colors duration-200 hover:border-amber-500/60">
                  {c}
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8">
          <Reveal>
            <Eyebrow>Why mulch, not haul</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-bone sm:text-3xl lg:text-4xl">
              Clearing without the scar.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.12}>
                <article data-testid={`lc-why-${i}`} className="group flex h-full flex-col border border-stone-800 bg-charcoal p-8 transition-colors duration-200 hover:border-amber-500/60">
                  <w.icon className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-normal text-bone sm:text-2xl">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-400 sm:text-base">{w.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8">
          <Reveal>
            <div className="border border-amber-500/40 bg-amber-500/5 p-8 sm:p-10">
              <Eyebrow>Different math here</Eyebrow>
              <p className="mt-4 max-w-3xl font-display text-xl font-bold uppercase leading-snug tracking-tight text-bone sm:text-2xl">
                Priced by the size of the area — <span className="text-amber-500">not a flat per-stump rate.</span>
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-400 sm:text-base">
                Same rules though: text photos or use the form, get a firm
                number back by text, contactless start to finish. Eric sets
                the price once he sees what you're dealing with — no invented
                rates, no "starting at" games.
              </p>
              <p data-testid="lc-founding-line" className="mt-5 max-w-2xl border-l-2 border-amber-500 bg-amber-500/10 px-4 py-3 text-sm font-semibold leading-relaxed text-amber-400 sm:text-base">
                Since this is a new side of the business, early jobs get
                priced accordingly — reach out and find out.
              </p>
            </div>
          </Reveal>
        </section>

        <section id="clearing-quote" data-testid="clearing-quote-section" className="mx-auto max-w-4xl scroll-mt-24 px-4 pb-24 sm:px-8">
          <Reveal>
            <Eyebrow>Clearing quote</Eyebrow>
            <h2 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-bone sm:text-3xl lg:text-4xl">
              Show me the mess.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-400 sm:text-base">
              A few photos and a rough size is all it takes. Or skip the form
              and text them straight to {PHONE}.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="lc-success-panel" className="mt-10 border border-green-700/50 bg-green-500/5 p-8 sm:p-10">
                <p className="font-display text-2xl font-black uppercase text-bone sm:text-3xl">
                  Request sent. <span className="text-amber-500">Nice.</span>
                </p>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone-400 sm:text-base">
                  Eric just got an email with your photos and the lay of the
                  land. Clearing is priced by the size of the area, so he'll
                  eyeball what you sent and text you a firm number — usually
                  within hours. Same contactless deal: no site visit unless
                  you ask for one.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a data-testid="lc-sms-fallback" href={`sms:${PHONE_RAW}`} className="flex items-center gap-2 bg-amber-500 px-5 py-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400">
                    <MessageSquareText className="h-4 w-4" /> Text Eric — {PHONE}
                  </a>
                  <button data-testid="lc-reset-btn" onClick={() => setSent(false)} className="flex items-center gap-2 border border-stone-700 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                    <RotateCcw className="h-3.5 w-3.5" /> Submit another
                  </button>
                </div>
              </div>
            ) : (
              <form data-testid="lc-quote-form" onSubmit={submit} className="mt-10 border border-stone-800 bg-charcoal p-6 sm:p-10">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="lc-name">Full name *</label>
                    <input id="lc-name" data-testid="lc-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Buckeye" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lc-phone">Phone / SMS *</label>
                    <input id="lc-phone" data-testid="lc-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="614-555-0139" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lc-email">Email (optional)</label>
                    <input id="lc-email" data-testid="lc-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lc-address">Property address & zip *</label>
                    <input id="lc-address" data-testid="lc-address-input" required className={inputCls} value={form.address} onChange={set("address")} placeholder="1234 High St, Columbus, OH 43201" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="lc-area">About how big is the area? *</label>
                    <select id="lc-area" data-testid="lc-area-select" required value={area} onChange={(e) => setArea(e.target.value)} className={`${inputCls} ${area ? "" : "text-stone-600"}`}>
                      <option value="" disabled>Pick the closest — rough is fine</option>
                      {AREAS.map((a) => (
                        <option key={a} value={a} className="text-bone">{a}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>What's growing there? (check all that fit)</span>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {CLEAR_ITEMS.map((label, i) => (
                        <button
                          key={label}
                          type="button"
                          data-testid={`lc-item-${i}`}
                          onClick={() => toggleItem(label)}
                          className={`min-h-[48px] border px-4 py-3 text-left font-mono text-xs uppercase tracking-[0.12em] transition-colors duration-150 ${
                            items.includes(label)
                              ? "border-amber-500 bg-amber-500/10 text-amber-500"
                              : "border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-200"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>How's the terrain?</span>
                    <div className="grid grid-cols-3 gap-2">
                      {TERRAINS.map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`lc-terrain-${t.toLowerCase()}`}
                          onClick={() => setTerrain(t)}
                          className={`min-h-[48px] border px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-150 ${
                            terrain === t
                              ? "border-amber-500 bg-amber-500/10 text-amber-500"
                              : "border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-200"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {terrain === "Steep" && (
                      <p data-testid="lc-steep-note" className="mt-3 border border-amber-600/50 bg-amber-500/10 px-4 py-3 font-mono text-[11px] leading-relaxed tracking-[0.05em] text-amber-400">
                        Steep is welcome — the mulcher is remote-controlled and goes where most crews won't set foot.
                      </p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>Photos — up to 4 (wide shot or aerial view helps)</span>
                    {photos.length > 0 && (
                      <div data-testid="lc-photo-previews" className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {photos.map((p, i) => (
                          <div key={p.id} data-testid={`lc-photo-thumb-${i}`} className="relative border border-stone-700">
                            <img src={p.preview} alt={`Clearing upload ${i + 1}`} className="h-20 w-full object-cover" />
                            <button type="button" data-testid={`lc-photo-remove-${i}`} onClick={() => setPhotos((cur) => cur.filter((x) => x.id !== p.id))} className="absolute right-1 top-1 border border-stone-600 bg-[#0c0a09]/85 p-1 text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500" aria-label={`Remove photo ${i + 1}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {photos.length < MAX_PHOTOS && (
                      <label data-testid="lc-photo-drop" className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-stone-700 bg-[#0c0a09] px-4 py-6 font-mono text-xs uppercase tracking-[0.15em] text-stone-400 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                        <ImagePlus className="h-4 w-4" />
                        {photos.length === 0 ? "Attach photos of the area" : `Add another (${photos.length}/${MAX_PHOTOS})`}
                        <input data-testid="lc-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={onPhoto} />
                      </label>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="lc-notes">Notes / anything else</label>
                    <textarea id="lc-notes" data-testid="lc-notes-input" rows="3" className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Back fence line, neighbor's side is marked, ground stays wet after rain…" />
                  </div>
                </div>

                <button
                  data-testid="lc-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="group mt-8 flex min-h-[52px] w-full items-center justify-center gap-2 bg-amber-500 px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60 sm:w-auto"
                >
                  <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  {sending ? "Sending…" : "Send it to Eric"}
                </button>
              </form>
            )}
          </Reveal>
        </section>
      </main>
      <Footer />
    </div>
  );
}
